// filters-bar.ts — Volet de filtres de la liste des actions (#actions) :
// scope Mes actions / Toutes, facettes RACI et équipe, périmètre (programme,
// projet, comité, organisation, plan d'action), filtres action (priorité,
// période de modification, terminées, cochées seulement), pliage et reset.
//
// Deuxième tranche de l'extraction des closures de `renderActionsPage`
// (audit dette technique 2026-09-07, point 16). Avant, le rendu lisait les
// vingt variables `f*` de la page par capture et le câblage les réassignait
// une à une. Le rendu est maintenant une fonction pure de
// `ActionsFilterState` (le type du domaine, déjà utilisé pour l'URL et le
// filtrage) ; le câblage ne touche à rien, il remonte un patch d'état à la
// page, qui reste seule à posséder ses variables et à décider de la suite
// (re-render, URL, re-fetch).
//
// Les règles couplées entre champs restent ici, calculées sur l'état reçu :
// changer de programme remet le projet à « Tous » et recalcule `fScope` ;
// passer la période en « personnalisée » pose la borne haute à aujourd'hui.

import { isDefaultFilterState, type ActionsFilterState } from '../../domain/actions-filter.js';
import { getVisibleProjects } from '../../domain/meeting-form.js';
import { facetCheckbox } from './render-page-fragments.js';

export interface FiltersBarRefData {
  userTeamIds: ReadonlyArray<number>;
  userMap: Record<number, { name: string }>;
  programs: ReadonlyArray<{
    id: number;
    name?: string;
    code?: string | null;
    projects?: Array<{ id: number }>;
  }>;
  projects: ReadonlyArray<{ id: number; name?: string; code?: string | null }>;
  committees: ReadonlyArray<{ id: number; code: string; name: string }>;
  structures: ReadonlyArray<{ id: number; code: string; label: string }>;
  phases: ReadonlyArray<{ id: number; code: string; title: string; plan_code: string }>;
}

export interface FiltersBarHandlers {
  /** Un filtre a changé : la page applique le patch, persiste l'URL,
   * re-rend le volet et la table. */
  onChange: (patch: Partial<ActionsFilterState>) => void;
  /** Une borne de la période personnalisée a changé : URL + table, sans
   * re-rendre le volet (l'utilisateur est en train de saisir). */
  onDateBoundChange: (patch: Partial<ActionsFilterState>) => void;
  /** Afficher/masquer les terminées change le JEU DE DONNÉES (les terminées
   * ne sont pas chargées par défaut) : la page persiste puis re-dispatch. */
  onShowDoneChange: (hideDone: boolean) => void;
  onReset: () => void;
  onCollapseChange: (collapsed: boolean) => void;
}

export interface FiltersBarContext {
  state: ActionsFilterState;
  collapsed: boolean;
  /** 'YYYY-MM-DD', borne haute par défaut de la période personnalisée. */
  today: string;
  ref: FiltersBarRefData;
  handlers: FiltersBarHandlers;
}

const SELECT_STYLE =
  'font-size:11px;padding:3px 6px;border:1px solid var(--border);border-radius:var(--radius);flex:1';
const ROW_STYLE = 'display:flex;align-items:center;gap:4px;font-size:11px';
const LABEL_STYLE = 'min-width:80px;color:var(--text3)';
const FIELDSET_STYLE =
  'border:1px solid var(--border);border-radius:var(--radius);padding:4px 10px 6px 10px;margin:0;height:100%';
const LEGEND_STYLE =
  'font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:var(--text3);padding:0 4px';

function selected(cond: boolean): string {
  return cond ? 'selected' : '';
}

/** Projets visibles : si un programme est sélectionné (ni 'all' ni 'none'),
 * la liste est restreinte aux projets de ce programme. Cohérent avec le
 * filtrage des actions qui se base sur la même règle. */
/** Projets du programme filtré. Règle partagée avec la fiche réunion
 * (domain/meeting-form) : « all »/« none » donnent un id nul, donc tous les
 * projets, comme un programme inconnu. */
export function visibleProjectsFor(
  fProgram: string,
  programs: FiltersBarRefData['programs'],
  projects: FiltersBarRefData['projects'],
): FiltersBarRefData['projects'] {
  return getVisibleProjects(parseInt(fProgram) || 0, programs, projects);
}

function raciFieldsetHtml(s: ActionsFilterState, ref: FiltersBarRefData): string {
  // Mode "Toutes les actions" (fAll) : RACI + Mon équipe grisés.
  const raciDisabled = s.fAll;
  const teamRowVisible = !s.fAll && ref.userTeamIds.length > 0;
  const showTeamSelector = teamRowVisible && s.fTeamMode;
  const teamOptsHtml = ref.userTeamIds
    .map((tid) => {
      const team = ref.userMap[tid];
      return team
        ? `<option value="${tid}" ${selected(String(s.fTeamId) === String(tid))}>${esc(team.name)}</option>`
        : '';
    })
    .join('');
  const teamSelectHtml = showTeamSelector
    ? `<select id="af-team-id" style="font-size:11px;padding:3px 6px;border:1px solid var(--border);border-radius:var(--radius);width:100%;margin-top:4px">
                  <option value="all" ${selected(s.fTeamId === 'all')}>${esc(t('pageActions.filter.teamAll'))}</option>
                  ${teamOptsHtml}
                </select>`
    : '';
  const teamColHtml = teamRowVisible
    ? `${facetCheckbox('af-team', esc(t('pageActions.filter.team')), s.fTeamMode, raciDisabled)}
              ${teamSelectHtml}`
    : `<div style="font-size:11px;color:var(--text3);font-style:italic">${esc(t('pageActions.filter.teamNone'))}</div>`;
  return `<fieldset style="${FIELDSET_STYLE};display:grid;grid-template-columns:1fr 1fr;gap:0 12px">
          <legend style="${LEGEND_STYLE};display:flex;align-items:center;gap:4px">${esc(t('pageActions.filter.raci'))}<button type="button" id="af-raci-help" class="help-tip" style="font-size:10px;width:14px;height:14px;line-height:14px" aria-label="${esc(t('actionModal.raci.fullHelpTitle'))}">?</button></legend>
          <div style="${raciDisabled ? 'opacity:.4;pointer-events:none' : ''};display:flex;flex-direction:column;gap:2px">
            ${facetCheckbox('af-performer', esc(t('pageActions.filter.raciR')), s.fPerformer, raciDisabled)}
            ${facetCheckbox('af-approver', esc(t('pageActions.filter.raciA')), s.fApprover, raciDisabled)}
            ${facetCheckbox('af-consulted', esc(t('pageActions.filter.raciC')), s.fConsulted, raciDisabled)}
            ${facetCheckbox('af-informed', esc(t('pageActions.filter.raciI')), s.fInformed, raciDisabled)}
          </div>
          <div>
            ${teamColHtml}
          </div>
        </fieldset>`;
}

function scopeSelectHtml(
  id: string,
  labelKey: string,
  value: string,
  allKey: string,
  noneKey: string,
  optsHtml: string,
): string {
  return `<label style="${ROW_STYLE}">
              <span style="${LABEL_STYLE}">${esc(t(labelKey))}</span>
              <select id="${id}" style="${SELECT_STYLE}">
                <option value="all"  ${selected(value === 'all')}>${esc(t(allKey))}</option>
                <option value="none" ${selected(value === 'none')}>${esc(t(noneKey))}</option>
                ${optsHtml}
              </select>
            </label>`;
}

function scopeFieldsetHtml(s: ActionsFilterState, ref: FiltersBarRefData): string {
  // Programmes : 'all' = Tous, 'none' = Aucun (= sans programme).
  const progOptsHtml = ref.programs
    .map(
      (p) =>
        `<option value="${p.id}" ${selected(String(s.fProgram) === String(p.id))}>${esc(p.code || '')} — ${esc(p.name)}</option>`,
    )
    .join('');
  const projOptsHtml = visibleProjectsFor(s.fProgram, ref.programs, ref.projects)
    .map(
      (p) =>
        `<option value="${p.id}" ${selected(s.fProject === String(p.id))}>${p.code ? esc(p.code) + ' - ' : ''}${esc(p.name)}</option>`,
    )
    .join('');
  // Comités triés par code (ordre alphabétique du code).
  const commOptsHtml = ref.committees
    .slice()
    .sort((a, b) => (a.code || '').localeCompare(b.code || ''))
    .map(
      (c) =>
        `<option value="${c.id}" ${selected(String(s.fCommittee) === String(c.id))}>${esc(c.code)} — ${esc(c.name)}</option>`,
    )
    .join('');
  // Organisation : structures triées par code, libellé affiché (code — libellé).
  const structOptsHtml = ref.structures
    .slice()
    .sort((a, b) => a.code.localeCompare(b.code))
    .map(
      (st) =>
        `<option value="${st.id}" ${selected(String(s.fStructure) === String(st.id))}>${esc(st.code)} — ${esc(st.label)}</option>`,
    )
    .join('');
  // Plans d'action : phases/volets triés par code de plan puis code de phase,
  // libellé « PLAN/PHASE — titre » pour rappeler le plan d'appartenance.
  const phaseOptsHtml = ref.phases
    .slice()
    .sort(
      (a, b) =>
        (a.plan_code || '').localeCompare(b.plan_code || '') || a.code.localeCompare(b.code),
    )
    .map(
      (ph) =>
        `<option value="${ph.id}" ${selected(String(s.fPhase) === String(ph.id))}>${esc(ph.plan_code)}/${esc(ph.code)} — ${esc(ph.title)}</option>`,
    )
    .join('');
  const programRowHtml = ref.programs.length
    ? scopeSelectHtml(
        'af-program',
        'pageActions.filter.program',
        s.fProgram,
        'pageActions.filter.programAll',
        'pageActions.filter.programNone',
        progOptsHtml,
      )
    : '';
  return `<fieldset style="${FIELDSET_STYLE}">
          <legend style="${LEGEND_STYLE}">${esc(t('pageActions.filter.scope'))}</legend>
          <div style="display:flex;flex-direction:column;gap:4px">
            ${programRowHtml}
            ${scopeSelectHtml('af-project', 'pageActions.filter.project', s.fProject, 'pageActions.filter.projectAll', 'pageActions.filter.projectNone', projOptsHtml)}
            ${scopeSelectHtml('af-committee', 'pageActions.filter.committee', s.fCommittee, 'pageActions.filter.committeeAll', 'pageActions.filter.committeeNone', commOptsHtml)}
            ${scopeSelectHtml('af-structure', 'pageActions.filter.organisation', s.fStructure, 'pageActions.filter.organisationAll', 'pageActions.filter.organisationNone', structOptsHtml)}
            ${scopeSelectHtml('af-phase', 'pageActions.filter.actionPlan', s.fPhase, 'pageActions.filter.actionPlanAll', 'pageActions.filter.actionPlanNone', phaseOptsHtml)}
          </div>
        </fieldset>`;
}

function actionFieldsetHtml(s: ActionsFilterState, today: string): string {
  // Priorité (sens métier : 0 = très haute → 4 = très basse).
  // Ordre demandé : Très haute en 1ʳᵉ ligne (haut de la liste), "Toutes"
  // en dernière ligne. La valeur par défaut reste 4 (Toutes).
  const prioOptsHtml = [
    ['0', t('pageActions.priority.0')],
    ['1', t('pageActions.priority.1plus')],
    ['2', t('pageActions.priority.2plus')],
    ['3', t('pageActions.priority.3plus')],
    ['4', t('pageActions.priority.all')],
  ]
    .map(
      ([v, l]) =>
        `<option value="${v}" ${selected(String(s.fPriorityMin) === v)}>${esc(l)}</option>`,
    )
    .join('');
  const dateModes: Array<[string, string]> = [
    ['all', 'pageActions.filter.dateAll'],
    ['today', 'pageActions.filter.dateToday'],
    ['yest', 'pageActions.filter.dateYest'],
    ['7d', 'pageActions.filter.date7d'],
    ['custom', 'pageActions.filter.dateCustom'],
  ];
  const dateOptsHtml = dateModes
    .map(([v, k]) => `<option value="${v}" ${selected(s.fDateMode === v)}>${esc(t(k))}</option>`)
    .join('');
  return `<fieldset style="${FIELDSET_STYLE}">
          <legend style="${LEGEND_STYLE}">${esc(t('pageActions.filter.action'))}</legend>
          <div style="display:flex;flex-direction:column;gap:4px">
            <label style="${ROW_STYLE}">
              <span style="${LABEL_STYLE}">${esc(t('pageActions.filter.priority'))}</span>
              <select id="af-priority-min" style="${SELECT_STYLE}">
                ${prioOptsHtml}
              </select>
            </label>
            <label style="${ROW_STYLE}">
              <span style="${LABEL_STYLE}">${esc(t('pageActions.filter.modified'))}</span>
              <select id="af-date-mode" style="${SELECT_STYLE}">
                ${dateOptsHtml}
              </select>
            </label>
            ${facetCheckbox('af-show-done', esc(t('pageActions.filter.showDone')), !s.fHideDone)}
            ${facetCheckbox('af-selected-only', esc(t('pageActions.filter.selectedOnly')), s.fSelectedOnly)}
            <div id="af-date-custom" style="display:${s.fDateMode === 'custom' ? 'flex' : 'none'};gap:4px;align-items:center;font-size:11px;flex-wrap:wrap">
              <span style="color:var(--text3)">${esc(t('pageActions.filter.dateFrom'))}</span>
              <input type="date" id="af-date-start" value="${esc(s.fDateStart || '')}" style="font-size:11px;padding:2px 4px;border:1px solid var(--border);border-radius:var(--radius);max-width:130px">
              <span style="color:var(--text3)">${esc(t('pageActions.filter.dateTo'))}</span>
              <input type="date" id="af-date-end" value="${esc(s.fDateEnd || today)}" style="font-size:11px;padding:2px 4px;border:1px solid var(--border);border-radius:var(--radius);max-width:130px">
            </div>
          </div>
        </fieldset>`;
}

/** Markup complet du volet. Pur : ne lit ni le DOM ni la page. */
export function filtersBarHtml(
  s: ActionsFilterState,
  collapsed: boolean,
  today: string,
  ref: FiltersBarRefData,
): string {
  const isDefault = isDefaultFilterState(s);
  return `<div id="act-filters" class="${collapsed ? 'collapsed' : ''}" style="background:var(--bg2);border:1px solid var(--border);border-radius:var(--radius);padding:8px 10px;margin-bottom:8px">
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:6px">
        <button id="af-collapse-toggle" type="button"
          title="${esc(t(collapsed ? 'pageActions.filter.expandTitle' : 'pageActions.filter.collapseTitle'))}"
          style="display:flex;align-items:center;gap:6px;background:none;border:none;cursor:pointer;padding:0;font:inherit;color:var(--text2)">
          <span style="font-size:11px;display:inline-block;transition:transform .15s;transform:rotate(${collapsed ? '-90deg' : '0deg'})">v</span>
          <span style="font-size:11px;font-weight:600">${esc(t('pageActions.filter.title'))}</span>
        </button>
        <button id="af-reset" class="btn btn-secondary btn-sm" ${isDefault ? 'disabled' : ''} title="${esc(t('pageActions.filter.resetTitle'))}" style="font-size:11px;padding:3px 10px">${esc(t('pageActions.filter.resetLabel'))}</button>
      </div>

      <div class="af-body">

      <!-- Radios scope (XOR) AU-DESSUS du grid des cadres.
           "Mes actions" en 1er (sélection par défaut), "Toutes" en 2e. -->
      <div style="display:flex;align-items:center;gap:16px;margin-bottom:6px;padding-left:2px">
        <label style="display:flex;align-items:center;gap:6px;font-size:12px;cursor:pointer">
          <input type="radio" name="af-scope-mode" value="mine" ${!s.fAll ? 'checked' : ''}>
          <span>${esc(t('pageActions.filter.modeMine'))}</span>
        </label>
        <label style="display:flex;align-items:center;gap:6px;font-size:12px;cursor:pointer" title="${esc(t('pageActions.filter.ownerScopeHint'))}">
          <input type="checkbox" id="af-owner-scope" ${s.fOwnerScope ? 'checked' : ''}>
          <span>${esc(t('pageActions.filter.ownerScope'))}</span>
        </label>
        <label style="display:flex;align-items:center;gap:6px;font-size:12px;cursor:pointer">
          <input type="radio" name="af-scope-mode" value="all" ${s.fAll ? 'checked' : ''}>
          <span>${esc(t('pageActions.filter.modeAll'))}</span>
        </label>
      </div>

      <!-- 3 colonnes : RACI | Périmètre | Filtres action -->
      <div style="display:grid;grid-template-columns:2fr 1fr 1fr;gap:12px">
        ${raciFieldsetHtml(s, ref)}
        ${scopeFieldsetHtml(s, ref)}
        ${actionFieldsetHtml(s, today)}
      </div>
      </div>
    </div>`;
}

/** Câble le volet rendu dans `el` (l'élément `#act-filters`). Chaque
 * changement remonte un patch d'état ; les règles couplées entre champs
 * sont calculées ici sur l'état reçu. */
export function wireFiltersBar(el: HTMLElement, ctx: FiltersBarContext): void {
  const { state: s, handlers, today } = ctx;
  let collapsed = ctx.collapsed;

  el.querySelector('#af-reset')?.addEventListener('click', () => handlers.onReset());

  // Toggle pliage du volet (avril 2026). Ne refait pas tout le render :
  // on toggle juste la classe et on persiste. Plus rapide qu'un re-render.
  el.querySelector('#af-collapse-toggle')?.addEventListener('click', () => {
    collapsed = !collapsed;
    handlers.onCollapseChange(collapsed);
    el.classList.toggle('collapsed', collapsed);
    const arrow = el.querySelector<HTMLElement>('#af-collapse-toggle span');
    if (arrow) arrow.style.transform = collapsed ? 'rotate(-90deg)' : 'rotate(0deg)';
    const btn = el.querySelector<HTMLButtonElement>('#af-collapse-toggle');
    if (btn)
      btn.title = t(
        collapsed ? 'pageActions.filter.expandTitle' : 'pageActions.filter.collapseTitle',
      );
  });

  el.querySelector('#af-raci-help')?.addEventListener('click', () => {
    const showHelp = (window as unknown as Record<string, unknown>)['showRaciHelpPopover'];
    if (typeof showHelp === 'function') (showHelp as () => void)();
  });

  const checked = (e: Event): boolean => (e.target as HTMLInputElement).checked;
  const value = (e: Event): string => (e.target as HTMLSelectElement).value;
  const on = (id: string, fn: (e: Event) => void): void => {
    el.querySelector('#' + id)?.addEventListener('change', fn);
  };

  el.querySelectorAll<HTMLInputElement>('input[name="af-scope-mode"]').forEach((r) => {
    r.addEventListener('change', () => handlers.onChange({ fAll: r.value === 'all' }));
  });
  on('af-owner-scope', (e) => handlers.onChange({ fOwnerScope: checked(e) }));
  on('af-performer', (e) => handlers.onChange({ fPerformer: checked(e) }));
  on('af-approver', (e) => handlers.onChange({ fApprover: checked(e) }));
  on('af-consulted', (e) => handlers.onChange({ fConsulted: checked(e) }));
  on('af-informed', (e) => handlers.onChange({ fInformed: checked(e) }));
  on('af-team', (e) => handlers.onChange({ fTeamMode: checked(e) }));
  on('af-team-id', (e) => handlers.onChange({ fTeamId: value(e) }));

  // Périmètre : `fScope` = au moins un des selects programme/projet n'est
  // pas « Tous ». Changer de programme remet le projet à « Tous ».
  on('af-program', (e) => {
    const fProgram = value(e);
    handlers.onChange({ fProgram, fProject: 'all', fScope: fProgram !== 'all' });
  });
  on('af-project', (e) => {
    const fProject = value(e);
    handlers.onChange({ fProject, fScope: s.fProgram !== 'all' || fProject !== 'all' });
  });
  on('af-committee', (e) => handlers.onChange({ fCommittee: value(e) }));
  on('af-structure', (e) => handlers.onChange({ fStructure: value(e) }));
  on('af-phase', (e) => handlers.onChange({ fPhase: value(e) }));
  on('af-priority-min', (e) => handlers.onChange({ fPriorityMin: parseInt(value(e)) }));
  on('af-date-mode', (e) => {
    const fDateMode = value(e) || 'all';
    const patch: Partial<ActionsFilterState> = { fDateMode };
    if (fDateMode === 'custom' && !s.fDateEnd) patch.fDateEnd = today;
    handlers.onChange(patch);
  });
  on('af-date-start', (e) => handlers.onDateBoundChange({ fDateStart: value(e) }));
  on('af-date-end', (e) => handlers.onDateBoundChange({ fDateEnd: value(e) }));
  on('af-show-done', (e) => handlers.onShowDoneChange(!checked(e)));
  on('af-selected-only', (e) => handlers.onChange({ fSelectedOnly: checked(e) }));
}

/** Rend le volet dans `host` (l'hôte `#act-filters-bar`), le câble, puis
 * pose les verrous du mode US : les facettes RACI, l'équipe et le scope
 * « Toutes les actions » sont cadenassés, l'US ne voit que ses actions. */
export function renderFiltersBarInto(host: HTMLElement, ctx: FiltersBarContext): void {
  const barHtml = filtersBarHtml(ctx.state, ctx.collapsed, ctx.today, ctx.ref);
  // eslint-disable-next-line no-restricted-syntax -- markup composé ci-dessus, valeurs utilisateur passées par esc().
  host.innerHTML = barHtml;
  const el = host.querySelector<HTMLElement>('#act-filters');
  if (el) wireFiltersBar(el, ctx);
  if (window.isSoloUser?.() && window.markLocked) {
    for (const id of ['af-performer', 'af-approver', 'af-consulted', 'af-informed', 'af-team']) {
      const cb = host.querySelector<HTMLElement>('#' + id);
      if (cb) window.markLocked(cb.parentElement || cb, 'solo');
    }
    const allRadio = host.querySelector<HTMLInputElement>(
      'input[name="af-scope-mode"][value="all"]',
    );
    if (allRadio) window.markLocked(allRadio.parentElement || allRadio, 'solo');
  }
}
